/**
 * <gallery-pan> — capped desktop product gallery that scrolls on hover.
 *
 * The gallery is capped to ~one viewport and pinned by <sticky-engine>; the
 * image stack scrolls *inside* the box (native overflow) when the pointer is
 * over it. The page length stays governed by the taller product-info column,
 * so visitors aren't forced to scroll the whole gallery to reach the sections
 * below. Page scroll never drives the gallery — only the wheel/trackpad while
 * hovered, at the visitor's own pace.
 *
 * The overflow itself is pure CSS (see assets/custom-css.css). This element
 * only adds the bits the thumbnails need:
 *   - isActive: is the gallery a scrollable capped box right now?
 *   - scrollItemIntoView: bring a clicked image into the box — used by
 *     gallery-peek.js and media-gallery.js.
 */
class GalleryPan extends HTMLElement {
  constructor() {
    super();
    this.desktop = window.matchMedia('(min-width: 990px)');
  }

  /** True when the gallery is a scrollable capped box (desktop, overflowing). */
  get isActive() {
    return this.desktop.matches && this.scrollHeight - this.clientHeight > 1;
  }

  /** Scroll the box so `el` sits at the top — no page movement. */
  scrollItemIntoView(el) {
    if (!el) return;

    if (!this.isActive) {
      // Not a capped box (mobile / fits without scrolling): fall back to moving
      // the page, accounting for the sticky header.
      const top = el.getBoundingClientRect().top + window.scrollY - (GalleryPan.headerOffset() + 24);
      window.scrollTo({ top, behavior: 'smooth' });
      return;
    }

    // Item position within the scroll content (offsetParent-independent).
    const top = el.getBoundingClientRect().top - this.getBoundingClientRect().top + this.scrollTop;
    this.scrollTo({ top, behavior: 'smooth' });
  }

  static headerOffset() {
    const header =
      document.querySelector('.section-header .header') || document.querySelector('.header');
    if (header && header.classList.contains('shopify-section-header-hidden')) return 16;
    const value = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    return Number.isNaN(value) ? (header ? header.offsetHeight : 0) : value;
  }
}

if (!window.customElements.get('gallery-pan')) {
  window.customElements.define('gallery-pan', GalleryPan);
}
